import { FunctionComponent, useState } from 'react'
import { Role } from '@/types/Role'
import { Daycare } from '@/types/Daycare'
import styles from './postDialog.module.scss'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import SectionSelector from '../sectionSelector/sectionSelector'
import DaycareSectionSelector from '../sectionSelector/DaycareSectionSelector'
import Dialog from '@/components/_custom/Dialog/Dialog'
import { Post, PollType } from '@/types/NewsFeed'
import DropZone from '../dropZone/dropZone'
import FilePreviewer from '../filePreview/filePreview'
import Desktop from '@/modules/desktopMobile/desktop'
import Mobile from '@/modules/desktopMobile/mobile'
import { useTranslation } from 'react-i18next'
import TextArea from '@/components/_custom/TextArea/textArea'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { Check, Draw } from '@mui/icons-material'
import { usePostForm } from '@/hooks/usePostForm'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import PollCreator from '../pollCreator/pollCreator'
import PostCategorySelector from '../postCategorySelector/postCategorySelector'

export type { FilePreview } from '@/hooks/usePostForm'

interface PostPopUpProps {
    open: boolean
    handleClose: (doRefetch: boolean) => void
    role: Role
    daycare: Daycare | undefined
    modifyPost: Post | null
    refetch: () => void
}

const PostDialog: FunctionComponent<PostPopUpProps> = ({
    open,
    handleClose,
    role,
    daycare,
    modifyPost,
    refetch
}: PostPopUpProps) => {
    const { t } = useTranslation()
    const { user } = useAuth()

    const {
        modify,
        formReady,
        mediasReady,
        postId,
        postMessage,
        postCategoryIds,
        postChildsModify,
        sections,
        parentsList,
        filePreview,
        disabledButton,
        selectedDaycares,
        selectedSections,
        selectedWithoutSection,
        visibleAll,
        poll,
        hasVotes,
        setPostMessage,
        setPostCategoryIds,
        setPostChilds,
        setFilePreview,
        setLoadingCreateMedia,
        setSelectedDaycares,
        setSelectedSections,
        setSelectedWithoutSection,
        setVisibleAll,
        setPoll,
        addNewFiles,
        handleSubmitCreate,
        handleUpdate,
        handleCloseAndCleanFile,
        addEmojiToPostMessage
    } = usePostForm({
        daycare,
        modifyPost,
        handleClose,
        refetch,
        trusteeDaycareIds:
            user?.role === Role.trustee && user.daycares.length > 1
                ? user.daycares.map((d: Daycare) => d.id)
                : undefined
    })

    const [emojiKey, setEmojiKey] = useState(0)

    function handleEmojiClick(emojiObject: EmojiClickData, reload: boolean): void {
        addEmojiToPostMessage(emojiObject.emoji)
        if (reload) {
            setEmojiKey((prev: number): number => prev + 1)
        }
    }

    const needToPreview: boolean = (modify && !mediasReady) || (!modify && filePreview.length > 0)

    function handleSelectCategoryIds(categoryIds: Array<string>): void {
        setPostCategoryIds(categoryIds.map((c) => Number(c)))
    }

    return (
        <Dialog open={open} handleClose={handleCloseAndCleanFile}>
            <DialogHeader title={modify ? t('modifyPost') : t('createPost')} parent={role == Role.parent} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.Layout}>
                    {/* ── Left column: message + media ── */}
                    <div className={styles.Left}>
                        <p className={styles.Label}>{t('message')}</p>
                        <div className={styles.MessageBlock}>
                            <TextArea
                                value={postMessage ?? ''}
                                placeholder={t('newsMessageHolder')}
                                maxLength={2000}
                                maxCount={2000}
                                onChange={setPostMessage}
                                className={styles.TextArea}
                            />
                            <EmojiPicker
                                key={emojiKey}
                                className={styles.EmojiBar}
                                open={true}
                                reactions={['1f600', '1f603', '1f604', '1f609', '1f606']}
                                onEmojiClick={(e) => handleEmojiClick(e, true)}
                                skinTonesDisabled
                                searchDisabled
                                width={'100%'}
                                height={350}
                                reactionsDefaultOpen={true}
                                onReactionClick={(e) => handleEmojiClick(e, false)}
                                previewConfig={{ showPreview: false }}
                            />
                        </div>
                        {!poll && (
                            <DropZone
                                postId={postId}
                                role={role}
                                title={t('medias')}
                                daycareId={daycare?.id ?? 0}
                                filePreview={filePreview}
                                addNewFiles={addNewFiles}
                                setLoadingCreateMedia={setLoadingCreateMedia}
                            />
                        )}
                        <div className={styles.PollToggleCard}>
                            <Checkbox
                                checked={!!poll}
                                onChange={(checked: boolean) =>
                                    checked
                                        ? setPoll({
                                              type: PollType.single_choice,
                                              options: [
                                                  { text: '', order: 0 },
                                                  { text: '', order: 1 }
                                              ]
                                          })
                                        : setPoll(undefined)
                                }
                                disabled={filePreview.length > 0}
                                text={t('poll')}
                            />
                            {filePreview.length > 0 && !poll && (
                                <p className={styles.PollHint}>{t('pollDisabledHint')}</p>
                            )}
                        </div>
                        <Mobile>
                            <FilePreviewer
                                daycareId={daycare?.id}
                                filePreview={filePreview}
                                setFilePreview={setFilePreview}
                                needToPreview={needToPreview}
                            />
                        </Mobile>
                    </div>
                    <div className={styles.Divider} />
                    <div className={styles.Right}>
                        {daycare?.id && (
                            <PostCategorySelector
                                daycareId={daycare?.id}
                                selectedCategories={postCategoryIds}
                                setSelectedCategoriesIds={handleSelectCategoryIds}
                            />
                        )}
                        <p className={styles.Label}>{t('sectionSelector')}</p>
                        <div className={styles.VisibleAllCard}>
                            <Checkbox
                                checked={visibleAll}
                                onChange={(checked: boolean) => setVisibleAll(checked)}
                                text={t('visibleAll')}
                            />
                        </div>
                        {formReady && !visibleAll && (
                            <>
                                {!modify && user?.role === Role.trustee && user.daycares.length > 1 ? (
                                    <DaycareSectionSelector
                                        daycares={user.daycares}
                                        selectedDaycares={selectedDaycares}
                                        setSelectedDaycares={setSelectedDaycares}
                                        selectedSections={selectedSections}
                                        setSelectedSections={setSelectedSections}
                                        selectedWithoutSection={selectedWithoutSection}
                                        setSelectedWithoutSection={setSelectedWithoutSection}
                                    />
                                ) : (
                                    parentsList.length > 0 && (
                                        <SectionSelector
                                            key={modifyPost?.id ?? 'new'}
                                            daycare={daycare}
                                            sections={sections}
                                            parents={parentsList}
                                            postChilds={postChildsModify}
                                            setPostChilds={setPostChilds}
                                            checkAllByDefault={!modify}
                                        />
                                    )
                                )}
                            </>
                        )}
                    </div>
                </div>
                <Desktop>
                    <div className={filePreview.length > 0 ? styles.PreviewDesktop : ''}>
                        <FilePreviewer
                            daycareId={daycare?.id}
                            filePreview={filePreview}
                            setFilePreview={setFilePreview}
                            needToPreview={needToPreview}
                        />
                    </div>
                </Desktop>
                {poll && (
                    <div className={styles.PollSection}>
                        <PollCreator poll={poll} onChange={setPoll} hasVotes={hasVotes} />
                    </div>
                )}
                <div className={styles.Footer}>
                    <ButtonTextIcon type={'close'} onClick={handleCloseAndCleanFile} className={styles.Button} />
                    {modify ? (
                        <ButtonTextIcon
                            type={'update'}
                            className={styles.Button}
                            onClick={handleUpdate}
                            disabled={disabledButton}
                        />
                    ) : (
                        <>
                            <ButtonTextIcon
                                type={'custom'}
                                customText={t('draft')}
                                customIcon={<Draw />}
                                className={styles.Button}
                                onClick={() => handleSubmitCreate(true)}
                                disabled={disabledButton}
                            />
                            {user?.currentDaycareRights.canAddNewsWithoutValidation && (
                                <ButtonTextIcon
                                    type={'custom'}
                                    customText={t('publish')}
                                    customIcon={<Check />}
                                    className={styles.Button}
                                    onClick={() => handleSubmitCreate(false)}
                                    disabled={disabledButton}
                                />
                            )}
                        </>
                    )}
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default PostDialog
